import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { familyInputSchema, type FamilyInput } from '@pirouette/shared';
import { useCreateFamily } from '@/data/families';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/admin/families/new')({
  component: NewFamily,
});

function NewFamily() {
  const navigate = useNavigate();
  const createFamily = useCreateFamily();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FamilyInput>({
    resolver: zodResolver(familyInputSchema),
    mode: 'onBlur',
    defaultValues: {
      address: { country: 'US' },
    },
  });

  async function onSubmit(values: FamilyInput) {
    try {
      const created = await createFamily.mutateAsync(values);
      toast({
        title: 'Family added',
        description: created.primary_guardian_name,
        variant: 'success',
      });
      await navigate({ to: '/admin/families/$id', params: { id: created.id } });
    } catch (err) {
      toast({
        title: "Couldn't save the family",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  return (
    <div className="space-y-8">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          New
        </p>
        <h1 className="mt-2">Add a family</h1>
        <p className="mt-2 max-w-prose text-[var(--color-ink-muted)]">
          The primary guardian's email is also the billing email by default. You can adjust that and
          add the second guardian after saving.
        </p>
      </header>

      <form
        onSubmit={(e) => void handleSubmit(onSubmit)(e)}
        noValidate
        className="grid max-w-3xl gap-6"
      >
        <fieldset className="surface-elevated grid gap-5 p-6">
          <legend className="px-2 tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
            Guardian
          </legend>

          <div>
            <Label htmlFor="guardian-name">Full name</Label>
            <Input
              id="guardian-name"
              autoComplete="name"
              aria-invalid={!!errors.primary_guardian_name}
              {...register('primary_guardian_name')}
            />
            {errors.primary_guardian_name && (
              <p role="alert" className="mt-1.5 text-[var(--color-danger)] text-[var(--text-sm)]">
                {errors.primary_guardian_name.message}
              </p>
            )}
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <Label htmlFor="guardian-email">Email</Label>
              <Input
                id="guardian-email"
                type="email"
                autoComplete="email"
                aria-invalid={!!errors.primary_guardian_email}
                {...register('primary_guardian_email')}
              />
              {errors.primary_guardian_email && (
                <p role="alert" className="mt-1.5 text-[var(--color-danger)] text-[var(--text-sm)]">
                  {errors.primary_guardian_email.message}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="guardian-phone">Phone</Label>
              <Input
                id="guardian-phone"
                type="tel"
                autoComplete="tel"
                aria-invalid={!!errors.primary_guardian_phone}
                {...register('primary_guardian_phone')}
              />
              {errors.primary_guardian_phone && (
                <p role="alert" className="mt-1.5 text-[var(--color-danger)] text-[var(--text-sm)]">
                  {errors.primary_guardian_phone.message}
                </p>
              )}
            </div>
          </div>

          <div>
            <Label htmlFor="billing-email">Billing email</Label>
            <Input
              id="billing-email"
              type="email"
              autoComplete="email"
              aria-invalid={!!errors.billing_email}
              aria-describedby="billing-email-hint"
              {...register('billing_email')}
            />
            <p
              id="billing-email-hint"
              className="mt-1.5 text-[var(--color-ink-subtle)] text-[var(--text-sm)]"
            >
              Optional — defaults to the guardian email.
            </p>
            {errors.billing_email && (
              <p role="alert" className="mt-1.5 text-[var(--color-danger)] text-[var(--text-sm)]">
                {errors.billing_email.message}
              </p>
            )}
          </div>
        </fieldset>

        <fieldset className="surface-elevated grid gap-5 p-6">
          <legend className="px-2 tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
            Address
          </legend>

          <div>
            <Label htmlFor="address-line1">Street</Label>
            <Input
              id="address-line1"
              autoComplete="address-line1"
              aria-invalid={!!errors.address?.line1}
              {...register('address.line1')}
            />
            {errors.address?.line1 && (
              <p role="alert" className="mt-1.5 text-[var(--color-danger)] text-[var(--text-sm)]">
                {errors.address.line1.message}
              </p>
            )}
          </div>

          <div className="grid gap-5 sm:grid-cols-3">
            <div>
              <Label htmlFor="address-city">City</Label>
              <Input
                id="address-city"
                autoComplete="address-level2"
                {...register('address.city')}
              />
            </div>
            <div>
              <Label htmlFor="address-region">State / Region</Label>
              <Input
                id="address-region"
                autoComplete="address-level1"
                {...register('address.region')}
              />
            </div>
            <div>
              <Label htmlFor="address-postal">Postal code</Label>
              <Input
                id="address-postal"
                autoComplete="postal-code"
                {...register('address.postalCode')}
              />
            </div>
          </div>
        </fieldset>

        <div className="flex gap-3">
          <Button type="submit" disabled={isSubmitting} aria-busy={isSubmitting}>
            {isSubmitting ? 'Saving…' : 'Save family'}
          </Button>
          <Link to="/admin/families">
            <Button variant="ghost" type="button">
              Cancel
            </Button>
          </Link>
        </div>
      </form>
    </div>
  );
}
